import React from "react";

import { Loader2 } from "lucide-react";

import { useRoScores } from "../../../Hooks/useRoConfig";
import { RoCompositeScore, RoDimensionScore } from "../../../Types/RoConfig";

const BAND_TILE: Record<RoCompositeScore["band"], string> = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-rose-500",
};

const BAND_BADGE: Record<RoDimensionScore["band"], string> = {
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  red: "bg-rose-50 text-rose-700 ring-rose-200",
};

const CompositeTile: React.FC<{
  title: string;
  subtitle: string;
  composite: RoCompositeScore;
}> = ({ title, subtitle, composite }) => (
  <div className="rounded-xl border border-gray-200 p-4">
    <p className="m-0 text-base font-semibold text-gray-900">{title}</p>
    <p className="mb-3 mt-0.5 text-xs text-gray-500">{subtitle}</p>
    <div className="flex items-center gap-3">
      <div
        className={`flex h-16 w-24 shrink-0 items-center justify-center rounded-lg text-2xl font-bold text-white ${BAND_TILE[composite.band]}`}
      >
        {composite.score.toFixed(1)}
      </div>
      <span className="text-sm font-medium text-gray-700">
        {composite.bandLabel}
      </span>
    </div>
  </div>
);

const DimensionScoreTable: React.FC<{
  title: string;
  rows: RoDimensionScore[];
}> = ({ title, rows }) => (
  <div className="mb-6 last:mb-0">
    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {title}
    </p>
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="border-b border-gray-200 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Dim
            </th>
            <th className="border-b border-gray-200 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Label
            </th>
            <th className="border-b border-gray-200 px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
              Share
            </th>
            <th className="border-b border-gray-200 px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
              n
            </th>
            <th className="border-b border-gray-200 px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
              Score
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={row.key}
              className="border-b border-gray-100 last:border-b-0"
            >
              <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                {row.key}
              </td>
              <td className="px-4 py-2.5">
                <span className="font-medium text-gray-900">{row.label}</span>
                {!row.included && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-inset ring-slate-200">
                    optional
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 text-right text-gray-700">
                {row.sharePct.toFixed(1)}%
              </td>
              <td className="px-4 py-2.5 text-right text-gray-500">{row.n}</td>
              <td className="px-4 py-2.5 text-right">
                <span
                  className={`inline-flex min-w-[3rem] justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${BAND_BADGE[row.band]}`}
                >
                  {row.score.toFixed(1)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const RoScoresPanel: React.FC = () => {
  const {
    riskComposite,
    opportunityComposite,
    riskDimensionScores,
    opportunityDimensionScores,
    loading,
    error,
  } = useRoScores();

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading R&amp;O scores…
      </div>
    );
  }

  if (error || !riskComposite || !opportunityComposite) {
    return (
      <p className="py-8 text-sm text-red-600">
        {error ?? "R&O scores are unavailable."}
      </p>
    );
  }

  return (
    <div>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <CompositeTile
          title="Risk Composite Score (RCS)"
          subtitle="Risk Health · 0 = critical (high risk) · 100 = minimal risk"
          composite={riskComposite}
        />
        <CompositeTile
          title="Opportunity Composite Score (OCS)"
          subtitle="0 = no opportunity · 100 = maximum expansion"
          composite={opportunityComposite}
        />
      </div>
      <DimensionScoreTable title="Risk dimensions" rows={riskDimensionScores} />
      <DimensionScoreTable
        title="Opportunity dimensions"
        rows={opportunityDimensionScores}
      />
    </div>
  );
};

export default RoScoresPanel;
